import { useState, useEffect, useCallback } from 'react';
import {
  Activity, Cpu, HardDrive, Database, Clock, AlertTriangle,
  TrendingUp, TrendingDown, Minus, RefreshCw, Loader2,
  Server, MemoryStick, Gauge, Heart, Zap
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import fetchWithTenant from '../utils/fetchWithTenant';

const COLORS = {
  primary: '#00d4aa',
  secondary: '#0ea5e9',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  muted: '#64748b',
  bg: 'var(--bg-page)',
  card: 'var(--bg-surface)',
  border: 'var(--border)',
};

const METRIC_TOOLTIPS = {
  cpu: 'How much processing power the server is using. High CPU means the server is working hard and may slow down.',
  memory: 'Total RAM used by the entire system (OS + all apps). If this gets too high, the server may start swapping to disk and slow down.',
  heap: 'RAM used by the Node.js application code and data. If heap grows continuously, it may indicate a memory leak.',
  disk: 'Storage space used on the server. When disk fills up, the database and logs can\'t write, causing crashes.',
  db: 'How long database queries take to respond. High latency means slow page loads and API responses.',
  eventLoop: 'How responsive the Node.js process is. High lag means the server is blocked and can\'t handle new requests quickly.',
};

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{
        width: 16, height: 16, borderRadius: '50%', border: '1px solid #475569',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 700, lineHeight: 1,
      }}>?</span>
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 8, padding: '8px 12px', background: 'var(--bg-surface)', border: '0.5px solid var(--border)',
          borderRadius: 8, fontSize: 12, color: '#cbd5e1', width: 240, lineHeight: 1.5,
          zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', textAlign: 'left',
          pointerEvents: 'none',
        }}>
          {text}
          <div style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0, borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent', borderTop: '6px solid #334155',
          }} />
        </div>
      )}
    </span>
  );
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const TrendIcon = ({ direction, size = 16 }) => {
  if (direction === 'increasing') return <TrendingUp size={size} color={COLORS.warning} />;
  if (direction === 'decreasing') return <TrendingDown size={size} color={COLORS.success} />;
  return <Minus size={size} color={COLORS.muted} />;
};

const StatusBadge = ({ status }) => {
  const colors = {
    ok: { bg: '#22c55e22', text: '#22c55e', label: 'Healthy' },
    degraded: { bg: '#f59e0b22', text: '#f59e0b', label: 'Degraded' },
    critical: { bg: '#ef444422', text: '#ef4444', label: 'Critical' },
  };
  const c = colors[status] || colors.ok;
  return (
    <span style={{ background: c.bg, color: c.text, padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
      {c.label}
    </span>
  );
};

const MetricCard = ({ icon: Icon, title, value, unit, subtext, color = COLORS.primary, trend, threshold, tooltip }) => {
  const pct = threshold ? Math.min((parseFloat(value) / threshold.critical) * 100, 100) : 0;
  const barColor = pct > 90 ? COLORS.danger : pct > 70 ? COLORS.warning : color;

  return (
    <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={18} color={color} />
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{title}</span>
          {tooltip && <InfoTooltip text={tooltip} />}
        </div>
        {trend && <TrendIcon direction={trend.direction} />}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
        {value}<span style={{ fontSize: 14, color: 'var(--text-secondary)', marginLeft: 4 }}>{unit}</span>
      </div>
      {threshold && (
        <div style={{ background: 'var(--bg-page)', borderRadius: 4, height: 4, marginBottom: 8, marginTop: 8 }}>
          <div style={{ background: barColor, height: '100%', borderRadius: 4, width: pct + '%', transition: 'width 0.5s' }} />
        </div>
      )}
      {subtext && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{subtext}</div>}
      {trend?.recommendation && (
        <div style={{ color: COLORS.warning, fontSize: 11, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={12} /> {trend.recommendation}
        </div>
      )}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}{p.unit || ''}
        </div>
      ))}
    </div>
  );
};

export default function ServerHealth() {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const [healthRes, metricsRes, summaryRes, trendsRes, alertsRes] = await Promise.all([
        fetch('/health/detailed').then(r => r.json()),
        fetch('/health/metrics?count=120').then(r => r.json()),
        fetch('/health/metrics/summary?minutes=60').then(r => r.json()),
        fetch('/health/metrics/trends?window=60').then(r => r.json()),
        fetch('/health/metrics/alerts').then(r => r.json()),
      ]);
      setHealth(healthRes);
      setMetrics(metricsRes.metrics?.map(m => ({
        time: formatTime(m.timestamp),
        cpu: m.cpu.pct,
        memory: m.memory.systemPct,
        heap: m.memory.heapPct,
        disk: m.disk.pct,
        eventLoop: m.eventLoopLagMs,
        dbLatency: m.dbLatencyMs,
        heapMB: Math.round(m.memory.heapUsed / 1048576),
        rssMB: Math.round(m.memory.rss / 1048576),
      })) || []);
      setSummary(summaryRes);
      setTrends(trendsRes);
      setAlerts(alertsRes.alerts || []);
    } catch (err) {
      console.error('Health fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: 'var(--text-secondary)' }}>
        <Loader2 size={24} className="animate-spin" style={{ marginRight: 8 }} /> Loading server health...
      </div>
    );
  }

  const THRESHOLDS = {
    cpu: { warn: 70, critical: 85 },
    memory: { warn: 75, critical: 85 },
    heap: { warn: 80, critical: 90 },
    disk: { warn: 80, critical: 90 },
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Heart size={24} color={COLORS.primary} /> Server Health
          </h1>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 13, marginTop: 4 }}>
            PID {health?.pid} · Up {health?.uptime?.human} · {health?.system?.hostname}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusBadge status={health?.status} />
          <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} /> Auto
          </label>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 12px', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {alerts.length > 0 && (
        <div style={{ background: '#ef444422', border: '1px solid #ef4444', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={16} /> Active Alerts ({alerts.length})
          </div>
          {alerts.map((a, i) => (
            <div key={i} style={{ color: '#fca5a5', fontSize: 13, marginLeft: 22 }}>
              {a.key}: {a.level} (count: {a.count})
            </div>
          ))}
        </div>
      )}

      {/* Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
        <MetricCard
          icon={Cpu} title="CPU Load" color={COLORS.secondary} tooltip={METRIC_TOOLTIPS.cpu}
          value={health?.cpu?.loadPct?.replace('%', '')} unit="%"
          subtext={`${health?.cpu?.cores} cores · Load ${health?.cpu?.loadAvg?.['1m']}`}
          trend={trends?.cpu} threshold={THRESHOLDS.cpu}
        />
        <MetricCard
          icon={Server} title="System Memory" color={COLORS.primary} tooltip={METRIC_TOOLTIPS.memory}
          value={health?.memory?.systemUsedPct?.replace('%', '')} unit="%"
          subtext={`${health?.memory?.systemFree} free of ${health?.memory?.systemTotal}`}
          trend={trends?.memory} threshold={THRESHOLDS.memory}
        />
        <MetricCard
          icon={Zap} title="Heap Memory" color="#a78bfa" tooltip={METRIC_TOOLTIPS.heap}
          value={health?.memory?.heapOfLimit?.replace('%', '') || health?.memory?.heapUtilization?.replace('%', '')} unit="%"
          subtext={`${health?.memory?.heapUsed} / ${health?.memory?.heapLimit || health?.memory?.heapTotal} limit`}
          trend={trends?.heap} threshold={THRESHOLDS.heap}
        />
        <MetricCard
          icon={HardDrive} title="Disk Usage" color="#f472b6" tooltip={METRIC_TOOLTIPS.disk}
          value={health?.system?.disk?.usedPct?.replace('%', '')} unit="%"
          subtext={`${health?.system?.disk?.used} used of ${health?.system?.disk?.total}`}
          trend={trends?.disk} threshold={THRESHOLDS.disk}
        />
        <MetricCard
          icon={Database} title="DB Latency" color={COLORS.warning} tooltip={METRIC_TOOLTIPS.db}
          value={health?.database?.latencyMs} unit="ms"
          subtext={summary ? `Avg ${summary.db?.avgLatencyMs}ms · Max ${summary.db?.maxLatencyMs}ms` : ''}
        />
        <MetricCard
          icon={Activity} title="Event Loop" color="#06b6d4" tooltip={METRIC_TOOLTIPS.eventLoop}
          value={health?.eventLoopLagMs} unit="ms"
          subtext={summary ? `Avg ${summary.eventLoop?.avgLagMs}ms · Max ${summary.eventLoop?.maxLagMs}ms` : ''}
        />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* CPU & Memory Chart */}
        <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>CPU & Memory</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={85} stroke={COLORS.danger} strokeDasharray="3 3" strokeOpacity={0.5} />
              <Area type="monotone" dataKey="cpu" name="CPU" stroke={COLORS.secondary} fill={COLORS.secondary} fillOpacity={0.1} />
              <Area type="monotone" dataKey="memory" name="Memory" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Heap & RSS Chart */}
        <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>Heap & RSS (MB)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="heapMB" name="Heap" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.1} />
              <Area type="monotone" dataKey="rssMB" name="RSS" stroke="#f472b6" fill="#f472b6" fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* DB Latency Chart */}
        <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>DB Latency (ms)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={500} stroke={COLORS.warning} strokeDasharray="3 3" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="dbLatency" name="DB Latency" stroke={COLORS.warning} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Event Loop Chart */}
        <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>Event Loop Lag (ms)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={200} stroke={COLORS.warning} strokeDasharray="3 3" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="eventLoop" name="Event Loop" stroke="#06b6d4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Capacity Projections */}
      {trends && (
        <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 24 }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 16px' }}>Capacity Projections</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {['cpu', 'memory', 'heap', 'disk'].map(key => {
              const t = trends[key];
              if (!t || t.error) return null;
              return (
                <div key={key} style={{ background: 'var(--bg-page)', borderRadius: 8, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'capitalize' }}>{key}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <TrendIcon direction={t.direction} size={14} />
                      {t.direction} ({t.growthPerHour > 0 ? '+' : ''}{t.growthPerHour}%/hr)
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    Current: {t.current}% · Avg: {t.avg}% · Range: {t.min}-{t.max}%
                  </div>
                  {t.projections?.warn && (
                    <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 6 }}>
                      ⚠ Warning ({t.projections.warn.value}%) in ~{t.projections.warn.daysUntil} days
                    </div>
                  )}
                  {t.projections?.critical && (
                    <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 4 }}>
                      🔴 Critical ({t.projections.critical.value}%) in ~{t.projections.critical.daysUntil} days
                    </div>
                  )}
                  {!t.projections?.warn && !t.projections?.critical && (
                    <div style={{ fontSize: 12, color: COLORS.success, marginTop: 6 }}>
                      ✓ No capacity concerns projected
                    </div>
                  )}
                  {t.recommendation && (
                    <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 6, padding: '6px 8px', background: '#f59e0b11', borderRadius: 6 }}>
                      💡 {t.recommendation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* System Info */}
      <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, border: `1px solid ${COLORS.border}` }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>System Info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 13 }}>
          {[
            ['Node', health?.nodeVersion],
            ['PID', health?.pid],
            ['Hostname', health?.system?.hostname],
            ['Platform', health?.system?.platform],
            ['CPU', health?.cpu?.model],
            ['Cores', health?.cpu?.cores],
            ['Started', health?.uptime?.startedAt ? new Date(health.uptime.startedAt).toLocaleString() : '-'],
            ['Uptime', health?.uptime?.human],
            ['System Uptime', health?.system?.uptimeHours + 'h'],
            ['Restarts (10m)', health?.restarts?.last10min],
            ['Environment', health?.env],
            ['Response', health?.responseMs + 'ms'],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
              <span style={{ color: 'var(--text-primary)' }}>{value ?? '-'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
